'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { ChevronDown } from '@/components/icons/ChevronDown';
import CloseIcon from '@/components/icons/Close';
import MenuIcon from '@/components/icons/Menu';
import Logo from '@/components/layout/Logo';
import MemberMenuOverlay from '@/components/layout/MemberMenuOverlay';
import MemberNavigation from '@/components/layout/MemberNavigation';
import { Button } from '@/components/ui/Button';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import useAuthContext from '@/hooks/useMsalAuthContext';
import { useNavigationMenus } from '@/hooks/useNavigationMenus';
import { cn } from '@/lib/ui/cn';
import type { NavItem, NavigationProps } from '@/types/shared';

const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sh-sky';

const ITEM_HOVER = 'lg:hover:bg-sh-cream';

function isActive(href: string, pathname: string): boolean {
  return href === pathname;
}

function computeAutoOpenAccordions(
  items: NavItem[],
  pathname: string
): Record<number, boolean> {
  const result: Record<number, boolean> = {};
  items.forEach((item, i) => {
    if (item.children?.some(c => c.href === pathname)) {
      result[i] = true;
    }
  });
  return result;
}

export default function Navigation({
  items,
  zenMode = false,
}: NavigationProps) {
  const pathname = usePathname();
  const { isAzureAuthenticated, handleLogout } = useAuthContext();
  const {
    closeMemberMenu,
    closeMenu,
    memberMenuOpen,
    open,
    openMemberMenu,
    toggleMenu,
  } = useNavigationMenus();

  const { pinnedItems, menuItems, ctaItem } = useMemo(() => {
    if (zenMode) {
      return {
        pinnedItems: [] as NavItem[],
        menuItems: [] as NavItem[],
        ctaItem: undefined as NavItem | undefined,
      };
    }
    const pinned = items.filter(i => i.pinned);
    return {
      pinnedItems: pinned,
      menuItems: items.filter(i => !i.pinned),
      ctaItem: pinned.find(i => i.label === 'Join Now'),
    };
  }, [items, zenMode]);

  const [openAccordions, setOpenAccordions] = useState<Record<number, boolean>>(
    () => computeAutoOpenAccordions(menuItems, pathname)
  );

  const [lastPathname, setLastPathname] = useState(pathname);
  if (lastPathname !== pathname) {
    setLastPathname(pathname);
    setOpenAccordions(computeAutoOpenAccordions(menuItems, pathname));
  }

  // Portal target only exists on the client; `open` starts false so the
  // initial SSR tree is unaffected either way.
  const mounted = typeof window !== 'undefined';

  function toggleAccordion(index: number) {
    setOpenAccordions(prev => ({ ...prev, [index]: !prev[index] }));
  }

  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  useFocusTrap({
    active: open,
    containerRef: dialogRef,
    initialFocusRef: closeButtonRef,
    onEscape: closeMenu,
  });

  const menuButton = !zenMode && (
    <Button
      variant='ghost'
      aria-label='Open menu'
      className='w-auto font-[400]'
      onClick={toggleMenu}
    >
      Menu
    </Button>
  );

  return (
    <>
      <header
        data-test='navbar'
        className={cn(
          'flex items-center justify-between',
          'px-5 py-5 md:px-22.5',
          'border-b-sh-gray/42 border-b',
          isAzureAuthenticated ? 'bg-sh-tan' : 'bg-sh-metal/10 backdrop-blur'
        )}
      >
        <div
          className={cn(
            'relative flex w-full shrink-0 items-center gap-1',
            isAzureAuthenticated ? 'justify-center' : 'justify-between'
          )}
        >
          <div
            className={cn(
              'flex items-center gap-4',
              isAzureAuthenticated && 'justify-center'
            )}
          >
            <Link href='/' aria-label='Go to homepage'>
              <Logo className='h-[14px] lg:h-[25px]' />
            </Link>

            <div className='hidden md:flex'>{menuButton}</div>
          </div>

          <div
            className={cn(
              'shrink-0 items-center gap-1',
              isAzureAuthenticated && 'hidden'
            )}
          >
            {pinnedItems.map(
              item =>
                item.href && (
                  <Button
                    key={item.label}
                    asChild
                    variant={item.variant}
                    aria-label={item.ariaLabel}
                    className={cn(
                      'hidden w-auto md:flex',
                      item.variant === 'dark' && 'bg-sh-black!',
                      item.variant === 'light' && 'bg-sh-white!'
                    )}
                  >
                    <Link href={item.href}>{item.label}</Link>
                  </Button>
                )
            )}

            <div className='md:hidden'>{menuButton}</div>
          </div>

          {isAzureAuthenticated && (
            <Button
              variant='ghost'
              aria-label='Open member menu'
              className='absolute top-1/2 right-0 flex size-12 -translate-y-1/2 items-center justify-center p-0 md:hidden'
              onClick={openMemberMenu}
            >
              <MenuIcon className='size-8' />
            </Button>
          )}
        </div>
      </header>

      {isAzureAuthenticated && (
        <nav aria-label='Member navigation'>
          <MemberNavigation onLogout={handleLogout} />
        </nav>
      )}

      {/* menu overlay — portaled to body so backdrop-blur on this header
          doesn't become its containing block. */}
      {open &&
        mounted &&
        createPortal(
          <div
            ref={dialogRef}
            role='dialog'
            aria-modal='true'
            aria-label='Navigation menu'
            className='bg-sh-paper fixed inset-0 z-[60] ml-auto flex h-dvh flex-col items-center gap-6 overflow-y-auto pt-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] lg:w-[418px]'
          >
            <div className='flex h-12 w-full shrink-0 items-center justify-between px-8 lg:justify-end lg:px-5'>
              <Link
                href='/'
                aria-label='Go to homepage'
                className='lg:hidden'
                // Next.js doesn't fire a route change for same-path clicks, so
                // close the overlay manually when we're already on the home page.
                onClick={() => isActive('/', pathname) && closeMenu()}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src='/shlogo.svg'
                  alt='Sollis Health'
                  width={220}
                  height={22}
                  className='h-5.5 w-55'
                />
              </Link>

              <button
                ref={closeButtonRef}
                aria-label='Close menu'
                onClick={closeMenu}
                className={cn(
                  '-mr-4 flex size-12 items-center justify-center rounded-full motion-safe:transition-colors lg:mr-0 lg:cursor-pointer lg:hover:bg-[#D9D9D9]',
                  FOCUS_RING
                )}
              >
                <CloseIcon className='text-sh-black size-6' strokeWidth={2} />
              </button>
            </div>

            {/* CTA */}
            {ctaItem?.href && (
              <div className='flex w-full shrink-0 items-center justify-center px-8'>
                <Link
                  href={ctaItem.href}
                  aria-label={ctaItem.ariaLabel}
                  className={cn(
                    'bg-sh-dark-gray flex h-[51px] w-full items-center justify-center rounded-full px-[30px] text-[21px] leading-[130%] font-bold text-white hover:opacity-90 motion-safe:transition-opacity',
                    FOCUS_RING
                  )}
                >
                  {ctaItem.label}
                </Link>
              </div>
            )}

            {/* Menu list */}
            <nav className='flex w-full flex-col items-start'>
              {menuItems.map((item, i) => {
                if (item.children?.length) {
                  return (
                    <AccordionItem
                      key={i}
                      item={item}
                      index={i}
                      isOpen={!!openAccordions[i]}
                      onToggle={() => toggleAccordion(i)}
                      pathname={pathname}
                      onClose={closeMenu}
                    />
                  );
                }
                if (!item.href) return null;
                return (
                  <MenuRow
                    key={i}
                    label={item.label}
                    href={item.href}
                    ariaLabel={item.ariaLabel}
                    pathname={pathname}
                    onClose={closeMenu}
                  />
                );
              })}
            </nav>
          </div>,
          document.body
        )}

      {/* Mobile member menu */}
      {memberMenuOpen && (
        <MemberMenuOverlay onClose={closeMemberMenu} onLogout={handleLogout} />
      )}
    </>
  );
}

function MenuRow({
  label,
  href,
  ariaLabel,
  pathname,
  onClose,
}: {
  label: string;
  href: string;
  ariaLabel?: string;
  pathname: string;
  onClose: () => void;
}) {
  const active = isActive(href, pathname);
  return (
    <Link
      href={href}
      aria-label={ariaLabel}
      aria-current={active ? 'page' : undefined}
      // Next.js won't fire a route change for same-path clicks, so close here.
      onClick={() => active && onClose()}
      className={cn(
        'sh-nav-item border-sh-dark-gray flex h-20 w-full items-center border-b px-8',
        active && 'bg-sh-cream',
        ITEM_HOVER,
        FOCUS_RING
      )}
    >
      {label}
    </Link>
  );
}

function AccordionItem({
  item,
  index,
  isOpen,
  onToggle,
  pathname,
  onClose,
}: {
  item: NavItem;
  index: number;
  isOpen: boolean;
  onToggle: () => void;
  pathname: string;
  onClose: () => void;
}) {
  const panelId = `nav-accordion-${index}`;
  return (
    <div className='border-sh-dark-gray flex w-full flex-col border-b'>
      <button
        type='button'
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={panelId}
        aria-label={item.ariaLabel}
        className={cn(
          'sh-nav-item flex h-20 w-full items-center justify-between px-8 py-2.5',
          ITEM_HOVER,
          'lg:cursor-pointer',
          FOCUS_RING
        )}
      >
        <span>{item.label}</span>
        <ChevronDown
          aria-hidden='true'
          className={cn(
            'text-sh-black h-[13px] w-6 shrink-0 motion-safe:transition-transform',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen && (
        <div id={panelId} className='flex w-full flex-col px-7 pb-4'>
          {item.children?.map(child => {
            if (!child.href) return null;
            const active = isActive(child.href, pathname);
            return (
              <Link
                key={child.href}
                href={child.href}
                aria-label={child.ariaLabel}
                aria-current={active ? 'page' : undefined}
                onClick={() => active && onClose()}
                className={cn(
                  'flex h-12 items-center pl-12',
                  'text-sh-black text-[28px] leading-[130%] font-medium lg:text-[26px]',
                  active && 'bg-sh-cream',
                  ITEM_HOVER,
                  FOCUS_RING
                )}
              >
                {child.label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
